"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Sparkles, Search, Layers, Cog, Target, Shield, Users, Moon, Compass } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PRINCIPLES = [
  { num: "01", icon: Sparkles, title: "Excellence Is a System, Not a Value", body: "If the operating steps cannot be written down, the outcome is luck, not excellence. What medicine systematized because lives depended on it, organizations must systematize because outcomes demand it.", accent: "red" },
  { num: "02", icon: Search, title: "Diagnose Before Treating", body: "The symptom is never the disease. Act on the root cause, or the failure recurs. The clinical method applied to organizations produces the precision that conventional approaches cannot.", accent: "cyan" },
  { num: "03", icon: Layers, title: "Triage", body: "Rank the work before doing the work. The inability to sequence is the inability to lead. When everything is urgent, nothing is solved well.", accent: "purple" },
  { num: "04", icon: Cog, title: "Run the Protocol; Keep the Judgment", body: "The system handles the routine. The operator handles the exception. Neither replaces the other. The mature operator loves the protocol and trusts it — and knows exactly when to put it down.", accent: "indigo" },
  { num: "05", icon: Target, title: "Own the Outcome, Not the Effort", body: "The result is the measure. Effort is a courtesy, not an achievement. Restore outcome accountability, and the culture transforms.", accent: "emerald" },
  { num: "06", icon: Shield, title: "First, Do No Harm", body: "Under pressure, the first discipline is to not make the situation worse. The most underrated operational skill. The oldest instruction in medicine, the most forgotten in leadership.", accent: "coral" },
  { num: "07", icon: Users, title: "Hire the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the market is mis-pricing. The 70% Operating Model is built on this principle.", accent: "amber-gold" },
  { num: "08", icon: Moon, title: "Build in the Dark", body: "Publicity is not impact. The things that endure are built without an audience. A decade of operating in discretion has proven this. The operator's discipline.", accent: "denim" },
];

const TEXT: Record<string, string> = {
  red: "text-red", purple: "text-purple", cyan: "text-cyan", emerald: "text-emerald", indigo: "text-indigo", coral: "text-coral", "amber-gold": "text-amber-gold", denim: "text-denim",
};

export function OperatingPrinciplesSection() {
  return (
    <Section id="operating-principles" ariaLabel="Operating Principles" background="muted">
      <SectionHeader
        icon={Compass}
        overline="The Creed"
        overlineClassName="text-purple"
        pillClassName="glass-tinted-purple border-purple/20"
        title={
          <>
            Eight Principles That Govern <span className="text-gradient-royal">Every Engagement.</span>
          </>
        }
        subtitle="Forged in clinical practice. Proven across a decade of sovereign-scale operations."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {PRINCIPLES.map((p, i) => (
          <motion.div
            key={p.num}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
            className={`group relative glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br from-${p.accent}/10 to-transparent`}
          >
            <div aria-hidden className="absolute -top-12 -right-12 size-40 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                  <p.icon className="size-5 text-white" />
                </div>
                <span className={`text-display-sm font-bold ${TEXT[p.accent]} opacity-30 group-hover:opacity-100 transition-opacity`}>
                  {p.num}
                </span>
              </div>
              <h3 className="text-heading-sm leading-tight mb-2">{p.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
